import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import signIn from "./helper functions/signIn";
import signOut from "./helper functions/signOut";
import { clearSessionExpiry } from "../../utils/session";

export const signInMember = createAsyncThunk("member/signIn", signIn);

export const signOutMember = createAsyncThunk("member/signOutMember", signOut);

// Cache of the get-visitor-detail response kept in sessionStorage so page
// refreshes don't refetch it (see signIn.js). Cleared whenever the cached
// data could go stale: profile edits, dependent add/delete, profile switch
// and sign-out.
const clearVisitorDetailCache = () =>
  sessionStorage.removeItem("visitorDetailCache");

const memberInitialState = {
  authToken: "",
  userID: "",
  businessEntityID: "",
  pID: "",
  email: "",
  givenName: "",
  familyName: "",
  displayName: "",
  brands: [],
  dashboardState: "",
  eventID: "",
  activeBrand: {
    ID: "",
    about: "",
    brandName: "",
    logo: "",
    languages: [],
    locations: [],
    category: [],
    mau: "",
    onlineStore: "",
    website: "",
    socialMedia: {},
  },
  visitorData: {
    ID: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    emailID: "",
    gender: "",
    onWhatsapp: "",
    faceData: "",
    dependentList: [],
    isDocVerified: "",
  },
  // Full raw get-visitor-detail response for the logged-in visitor. Populated at
  // sign-in and reused by pages (see useVisitorDetail) so they don't refetch
  // when redux already has the data. Null = not loaded / invalidated.
  visitorDetail: null,
  businessData: {
    name: "",
    logo: "https://dolphinchat-chat.s3.ap-south-1.amazonaws.com/mpsedc/digipass/events1761823051176_1761823053338_Book_My_Show_Logo.png",
    color: "",
    image: "",
    fontFamily: "",
  },
};

export const memberSlice = createSlice({
  name: "member",
  initialState: memberInitialState,
  reducers: {
    setActiveBrand(state, { payload }) {
      state.activeBrand.ID = parseInt(payload.brand_id);
      state.activeBrand.about = payload.about;
      state.activeBrand.brandName = payload.brand_name;
      state.activeBrand.logo = payload.logo;
      state.activeBrand.languages = payload.languages;
      state.activeBrand.locations = payload.audience_location;
      state.activeBrand.category = payload.site_category;
      state.activeBrand.mau = payload.mau;
      state.activeBrand.onlineStore = payload.online_store;
      state.activeBrand.website = payload.website;
      state.activeBrand.socialMedia = payload.social_media_handles;
    },

    setUserName(state, { payload }) {
      state.visitorData.firstName = payload;
    },

    setUserDetails(state, { payload }) {
      clearVisitorDetailCache();
      state.visitorDetail = null;
      sessionStorage.setItem(
        "visitorID",
        payload?.userData[0]?._id || payload?.userData[0]?.ID
      );
      let newVisitorData = {
        ID: payload?.userData[0]?._id || payload?.userData[0]?.ID,
        firstName:
          payload?.userData[0]?.first_name ||
          payload?.userData[0]?.firstName ||
          "",
        lastName:
          payload?.userData[0]?.last_name ||
          payload?.userData[0]?.lastName ||
          "",
        gender: payload?.userData[0]?.gender || "",
        emailID:
          payload?.userData[0]?.email_id || payload?.userData[0]?.emailID,
        faceData: "",
        onWhatsapp: "",
        phoneNumber:
          payload?.userData[0]?.phone_number ||
          payload?.userData[0]?.phoneNumber,
        dependentList: payload?.dependentList || [],
      };

      state.visitorData = newVisitorData;
    },

    setBrands(state, { payload }) {
      state.brands.push(payload);
    },

    setDisplayName(state, { payload }) {
      state.displayName = payload;
    },

    setDashboardState(state, { payload }) {
      state.dashboardState = payload;
    },

    setAccessToken: (state, { payload }) => {
      state.authToken = payload;
    },

    setDependent(state, { payload }) {
      state.visitorData.dependentList.push(payload);
      clearVisitorDetailCache();
      state.visitorDetail = null;
    },

    setBusinessData(state, { payload }) {
      state.businessData = { ...state.businessData, ...payload };
    },

    setIsDocVerifiedStatus(state, { payload }) {
      state.visitorData.isDocVerified = payload.is_doc_verified;
    },

    updateVisitorData(state, { payload }) {
      state.visitorData = { ...state.visitorData, ...payload };
      clearVisitorDetailCache();
      state.visitorDetail = null;
    },

    // Store the full raw get-visitor-detail response in redux. Pass null to
    // invalidate it (e.g. after a mutation) so the next read refetches.
    setVisitorDetail(state, { payload }) {
      state.visitorDetail = payload;
    },

    // Background revalidation result from signIn (stale-while-revalidate).
    // Patches state with fresh server data but does NOT clear the cache, since
    // the cache was just refreshed with this same data.
    syncVisitorData(state, { payload }) {
      state.visitorData = { ...state.visitorData, ...payload };
    },

    deleteAccessToken: () => {
      localStorage.removeItem("authToken");
      sessionStorage.removeItem("authToken");
      localStorage.removeItem("auth_token_with_guard");
      sessionStorage.removeItem("auth_token_with_guard");
      clearSessionExpiry();
      clearVisitorDetailCache();
      sessionStorage.removeItem("businessTheme");

      const checkInRoute = [
        "/sign-up",
        // "/sign-in",
        // "/manual-entry",
        // "/resend-notification",
        // "/manual-verification",
        // "/main-gate-entry-back",
        // "/main-gate-entry-front",
        "/add-booking",
      ].filter((item) => window.location.pathname == item);

      if (!Boolean(checkInRoute.length)) {
        window.location.href = "/login";
      } else {
        window.location.href = "/sign-in";
      }

      return memberInitialState;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(signInMember.fulfilled, (_, { payload }) => {
        return payload;
      })
      .addCase(signInMember.rejected, () => {
        localStorage.removeItem("authToken");
        sessionStorage.removeItem("authToken");
        localStorage.removeItem("auth_token_with_guard");
        sessionStorage.removeItem("auth_token_with_guard");
        clearSessionExpiry();
        clearVisitorDetailCache();
        sessionStorage.removeItem("businessTheme");

        return { ...memberInitialState, authToken: "" };
      })
      .addCase(signOutMember.fulfilled, () => {
        localStorage.removeItem("authToken");
        sessionStorage.removeItem("authToken");
        localStorage.removeItem("auth_token_with_guard");
        sessionStorage.removeItem("auth_token_with_guard");
        clearSessionExpiry();
        sessionStorage.removeItem("brandType");
        localStorage.removeItem(
          "dashboardState" + sessionStorage.getItem("activeBrand")
        );
        sessionStorage.removeItem("activeBrand");
        sessionStorage.removeItem("businessEntityID");
        clearVisitorDetailCache();
        sessionStorage.removeItem("businessTheme");

        return memberInitialState;
      });
  },
});

export const {
  setDashboardState,
  deleteAccessToken,
  setBrands,
  setActiveBrand,
  setDisplayName,
  setUserName,
  setAccessToken,
  setDependent,
  setUserDetails,
  setBusinessData,
  setIsDocVerifiedStatus,
  updateVisitorData,
  syncVisitorData,
  setVisitorDetail,
} = memberSlice.actions;

export default memberSlice.reducer;
